// src/utils/locationUtils.js
// Utilities per la gestione delle posizioni di timbratura

import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * ✅ IMPLEMENTAZIONE CORRETTA
 * Recupera la posizione assegnata a un dispositivo kiosk
 * 
 * @param {string} deviceId - ID del dispositivo (es. "tablet-ingresso-01")
 * @returns {string|null} - ID della posizione (cantiere/officina) o null
 */
export const getKioskDeviceLocation = async (deviceId) => {
    try {
      console.log(`🔍 [getKioskDeviceLocation] START`);
      console.log(`   deviceId: "${deviceId}"`);
      console.log(`   deviceId type: ${typeof deviceId}`);
      console.log(`   deviceId length: ${deviceId?.length}`);
      
      // Validazione input
      if (!deviceId || typeof deviceId !== 'string' || deviceId.trim() === '') {
        console.warn(`⚠️ [getKioskDeviceLocation] deviceId non valido:`, deviceId);
        return null;
      }
      
      // ✅ METODO CONSIGLIATO: Usa doc() + getDoc()
      // Questo cerca direttamente il documento usando il deviceId come ID del documento
      const deviceRef = doc(db, 'scanDevices', deviceId);
      console.log(`🔍 [getKioskDeviceLocation] Tentativo getDoc per: scanDevices/${deviceId}`);
      
      const deviceSnap = await getDoc(deviceRef);
      console.log(`🔍 [getKioskDeviceLocation] deviceSnap.exists(): ${deviceSnap.exists()}`);
      
      if (!deviceSnap.exists()) {
        console.warn(`⚠️ [getKioskDeviceLocation] Dispositivo NON TROVATO in Firestore: ${deviceId}`);
        console.warn(`⚠️ Verifica che il documento esista in: scanDevices/${deviceId}`);
        return null;
      }
      
      const deviceData = deviceSnap.data();
      console.log(`🔍 [getKioskDeviceLocation] Documento trovato:`, {
        deviceName: deviceData.deviceName,
        active: deviceData.active,
        assignedLocation: deviceData.assignedLocation,
        // Log tutti i campi per debug
        allFields: Object.keys(deviceData)
      });
      
      const assignedLocation = deviceData.assignedLocation;
      
      if (!assignedLocation) {
        console.warn(`⚠️ [getKioskDeviceLocation] Campo 'assignedLocation' MANCANTE o NULL`);
        console.warn(`⚠️ Vai su Admin → Dispositivi → Modifica "${deviceData.deviceName || deviceId}" → Assegna posizione`);
        return null;
      }
      
      console.log(`✅ [getKioskDeviceLocation] Posizione trovata: ${assignedLocation}`);
      return assignedLocation;
      
    } catch (error) {
      console.error(`❌ [getKioskDeviceLocation] Errore durante recupero:`, error);
      console.error(`   Error name: ${error.name}`);
      console.error(`   Error message: ${error.message}`);
      console.error(`   Error code: ${error.code}`);
      
      // Non lanciare errore, ritorna null per non bloccare la timbratura
      return null;
    }
  };

/**
 * Recupera la lista di cantieri attivi + Officina Pistavecchia
 * @returns {Promise<Array>} - Array di location disponibili
 */
export const getAvailableLocations = async () => {
  try {
    console.log('📍 getAvailableLocations: Recupero locations disponibili');
    
    // Carica cantieri attivi
    const cantieriRef = collection(db, 'cantieri');
    const q = query(
      cantieriRef,
      where('stato', '==', 'Attivo')
    );
    
    const snapshot = await getDocs(q);
    
    const locations = [
      // Officina Pistavecchia sempre disponibile
      {
        id: 'officina-pistavecchia',
        nome: 'Officina Pistavecchia',
        tipo: 'officina'
      }
    ];
    
    // Aggiungi cantieri attivi
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      locations.push({
        id: doc.id,
        nome: data.nomeCommessa || data.nome || 'Cantiere senza nome',
        tipo: 'cantiere',
        indirizzo: data.indirizzo || ''
      });
    });
    
    // Ordina alfabeticamente
    locations.sort((a, b) => a.nome.localeCompare(b.nome));
    
    console.log(`✅ Trovate ${locations.length} locations disponibili`);
    return locations;
    
  } catch (error) {
    console.error('❌ Errore recupero locations:', error);
    return [{
      id: 'officina-pistavecchia',
      nome: 'Officina Pistavecchia',
      tipo: 'officina'
    }];
  }
};

/**
 * Recupera il nome di una location dato il suo ID
 * @param {string} locationId - ID della location
 * @returns {Promise<string>} - Nome della location
 */
export const getLocationName = async (locationId) => {
  try {
    if (locationId === 'officina-pistavecchia') {
      return 'Officina Pistavecchia';
    }
    
    const cantiereRef = doc(db, 'cantieri', locationId);
    const docSnap = await getDoc(cantiereRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.nomeCommessa || data.nome || 'Cantiere senza nome';
    }
    
    return 'Posizione non specificata';
    
  } catch (error) {
    console.error('❌ Errore recupero nome location:', error);
    return 'Posizione non specificata';
  }
};

/**
 * Valida una location ID
 * @param {string} locationId - ID da validare
 * @returns {Promise<boolean>} - true se valida
 */
export const validateLocationId = async (locationId) => {
  if (!locationId) return false;
  
  if (locationId === 'officina-pistavecchia') {
    return true;
  }
  
  try {
    const cantiereRef = doc(db, 'cantieri', locationId);
    const docSnap = await getDoc(cantiereRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.stato === 'Attivo';
    }
    
    return false;
    
  } catch (error) {
    console.error('❌ Errore validazione location:', error);
    return false;
  }
};